// @ts-nocheck
import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Scan,
  Barcode,
  Truck,
  Box,
  CheckCircle2,
  AlertCircle,
  Volume2,
  VolumeX,
  Camera,
  Trash2,
  Loader2,
  RefreshCw,
  Clock,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { formatNumber } from "@/lib/utils";

interface CartonScanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryId: string;
  deliveryCode?: string;
  customerName?: string;
  onSuccess?: () => void;
}

// ── Web Audio API Beep Synthesizer ───────────────
function playBeep(type: "success" | "error" | "warning") {
  try {
    const AudioContextClass =
      window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    const ctx = new AudioContextClass();

    if (type === "success") {
      // Pleasant double chirp
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = "sine";
      osc1.frequency.setValueAtTime(987.77, ctx.currentTime); // B5
      gain1.gain.setValueAtTime(0.2, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.08);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = "sine";
      osc2.frequency.setValueAtTime(1318.51, ctx.currentTime + 0.09); // E6
      gain2.gain.setValueAtTime(0.25, ctx.currentTime + 0.09);
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(ctx.currentTime + 0.09);
      osc2.stop(ctx.currentTime + 0.2);
    } else if (type === "error") {
      // Low rejection buzz
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(220, ctx.currentTime);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
    } else {
      // Warning beep
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
    }
  } catch (e) {
    console.error("Audio feedback error:", e);
  }
}

export default function CartonScanDialog({
  open,
  onOpenChange,
  deliveryId,
  deliveryCode,
  customerName,
  onSuccess,
}: CartonScanDialogProps) {
  const queryClient = useQueryClient();
  const [barcodeInput, setBarcodeInput] = useState("");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [sessionScans, setSessionScans] = useState<
    Array<{
      code: string;
      cartonIndex?: number;
      totalCartons?: number;
      quantity?: number;
      scannedAt: Date;
      status: "SUCCESS" | "FAILED";
      message?: string;
    }>
  >([]);

  const inputRef = useRef<HTMLInputElement>(null);

  // Auto focus input when dialog opens or after every scan
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Fetch all cartons currently assigned to this delivery
  const {
    data: deliveryCartonsData,
    isLoading: loadingCartons,
    refetch: refetchCartons,
  } = useQuery({
    queryKey: ["delivery-cartons", deliveryId],
    queryFn: async () => {
      return await trpcClient.packingLabels.listByDelivery.query({
        deliveryId,
      });
    },
    enabled: open && !!deliveryId,
  });

  const cartons = deliveryCartonsData?.items || [];
  const totalCount = cartons.length;
  const dispatchedCount = cartons.filter((c: any) => c.isDispatched).length;
  const totalQty = cartons.reduce((sum: number, c: any) => sum + (c.quantity || 0), 0);

  // Scan Mutation
  const scanMutation = useMutation({
    mutationFn: async (code: string) => {
      return await trpcClient.packingLabels.scanForDelivery.mutate({
        labelCode: code.trim(),
        deliveryId,
      });
    },
    onSuccess: (res: any) => {
      if (soundEnabled) playBeep("success");
      toast.success(res.message || `Đã quét thùng carton ${res.label?.code} lên xe!`);

      setSessionScans((prev) => [
        {
          code: res.label?.code || barcodeInput,
          cartonIndex: res.label?.cartonIndex,
          totalCartons: res.label?.totalCartons,
          quantity: res.label?.quantity,
          scannedAt: new Date(),
          status: "SUCCESS",
          message: `Thùng #${res.label?.cartonIndex || "—"} (${formatNumber(res.label?.quantity || 0)} SP)`,
        },
        ...prev,
      ]);

      queryClient.invalidateQueries({ queryKey: ["delivery-cartons", deliveryId] });
      queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      queryClient.invalidateQueries({ queryKey: ["delivery", deliveryId] });
      if (onSuccess) onSuccess();
    },
    onError: (err: any) => {
      if (soundEnabled) playBeep("error");
      const errorMsg = err?.message || "Lỗi quét mã thùng carton";
      toast.error(errorMsg);

      setSessionScans((prev) => [
        {
          code: barcodeInput,
          scannedAt: new Date(),
          status: "FAILED",
          message: errorMsg,
        },
        ...prev,
      ]);
    },
    onSettled: () => {
      setBarcodeInput("");
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    },
  });

  // Handle barcode submission
  const handleScanSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!barcodeInput.trim()) return;
    scanMutation.mutate(barcodeInput.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] flex flex-col p-0 overflow-hidden bg-white border-slate-200">
        {/* Header */}
        <DialogHeader icon={Scan} className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <DialogTitle className="flex items-center gap-2">
                Trạm Quét Mã Vạch Thùng Carton Xếp Xe
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[11px]">
                  {deliveryCode || deliveryId}
                </Badge>
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                {customerName ? `Khách hàng: ${customerName} • ` : ""}
                Hỗ trợ súng bắn barcode USB/Bluetooth & nhập tay
              </DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 h-8 w-8"
              title={soundEnabled ? "Tắt âm thanh" : "Bật âm thanh"}
            >
              {soundEnabled ? (
                <Volume2 className="h-4 w-4 text-emerald-700" />
              ) : (
                <VolumeX className="h-4 w-4 text-slate-500" />
              )}
            </Button>
          </div>
        </DialogHeader>

        {/* Realtime KPI Bar */}
        <div className="bg-slate-50 p-4 border-b border-slate-200 grid grid-cols-3 gap-4">
          <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              Tổng Thùng Đã Gán
            </span>
            <div className="text-xl font-bold font-mono text-slate-900 mt-0.5">
              {totalCount} <span className="text-xs font-normal text-slate-500">thùng</span>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg border border-emerald-200 shadow-2xs">
            <span className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Đã Lên Xe
            </span>
            <div className="text-xl font-bold font-mono text-emerald-600 mt-0.5">
              {dispatchedCount}{" "}
              <span className="text-xs font-normal text-emerald-600/70">
                / {totalCount > 0 ? totalCount : "—"}
              </span>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg border border-indigo-200 shadow-2xs">
            <span className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wider flex items-center gap-1">
              <Box className="h-3.5 w-3.5 text-indigo-600" /> Tổng SL Sản Phẩm
            </span>
            <div className="text-xl font-bold font-mono text-indigo-600 mt-0.5">
              {formatNumber(totalQty)}{" "}
              <span className="text-xs font-normal text-indigo-600/70">hộp</span>
            </div>
          </div>
        </div>

        {/* Barcode Scanner Input Form */}
        <div className="p-4 bg-white border-b border-slate-200">
          <form onSubmit={handleScanSubmit} className="flex gap-2">
            <div className="relative flex-1">
              <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                ref={inputRef}
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                placeholder="Quét hoặc nhập mã tem thùng carton (VD: LBL-260906-0001)..."
                className="pl-10 pr-4 h-12 text-base font-mono border-indigo-300 focus-visible:ring-indigo-500 shadow-xs"
                disabled={scanMutation.isPending}
                autoFocus
              />
            </div>
            <Button
              type="submit"
              disabled={scanMutation.isPending || !barcodeInput.trim()}
              className="h-12 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm shadow-xs"
            >
              {scanMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Đang kiểm tra...
                </>
              ) : (
                <>
                  <Scan className="h-4 w-4 mr-2" /> Quét Thùng (Enter)
                </>
              )}
            </Button>
          </form>
          <div className="flex items-center justify-between text-[11px] text-slate-400 mt-2 px-1">
            <span>Mẹo: Cắm súng bắn barcode USB/Bluetooth và bấm cò trực tiếp. Hệ thống tự nhận diện và chuyển trạng thái "Đã Lên Xe".</span>
            <span className="font-mono text-indigo-600 font-medium">Auto-focus ON</span>
          </div>
        </div>

        {/* Scan Log and Carton List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {sessionScans.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-indigo-600" /> Lịch sử quét phiên làm việc hiện tại ({sessionScans.length})
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSessionScans([])}
                  className="h-6 text-[11px] text-slate-400 hover:text-slate-600"
                >
                  Xoá nhật ký
                </Button>
              </div>
              <div className="space-y-1.5">
                {sessionScans.map((scan, idx) => (
                  <div
                    key={idx}
                    className={`p-2.5 rounded-lg border text-xs flex items-center justify-between transition-all ${
                      scan.status === "SUCCESS"
                        ? "bg-emerald-50/60 border-emerald-200 text-emerald-900"
                        : "bg-rose-50/60 border-rose-200 text-rose-900"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {scan.status === "SUCCESS" ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-rose-600 shrink-0" />
                      )}
                      <div>
                        <span className="font-mono font-bold">{scan.code}</span>
                        {scan.message && (
                          <span className="ml-2 text-slate-600">{scan.message}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] text-slate-400 font-mono">
                      {scan.scannedAt.toLocaleTimeString("vi-VN")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Current Delivery Cartons Table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <Box className="h-3.5 w-3.5 text-slate-600" /> Danh sách thùng carton thuộc phiếu giao hàng ({cartons.length})
              </h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchCartons()}
                className="h-6 text-[11px] text-slate-600"
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Làm mới
              </Button>
            </div>

            {loadingCartons ? (
              <div className="text-center py-8 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-indigo-600" />
                Đang tải dữ liệu thùng carton...
              </div>
            ) : cartons.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200 text-slate-400 text-xs">
                Chưa có thùng carton nào được gán vào chuyến xe này.
                <br />
                Hãy quét mã vạch tem thùng (`LBL-...`) để gán tự động.
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="py-2 px-3">Mã Thùng (Barcode)</th>
                      <th className="py-2 px-3">Thứ Tự</th>
                      <th className="py-2 px-3 text-right">Số Lượng</th>
                      <th className="py-2 px-3">Trạng Thái</th>
                      <th className="py-2 px-3">Thời Điểm Quét</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {cartons.map((carton: any) => (
                      <tr
                        key={carton.id}
                        className={`hover:bg-slate-50/80 ${
                          carton.isDispatched ? "bg-emerald-50/20" : ""
                        }`}
                      >
                        <td className="py-2 px-3 font-mono font-bold text-slate-900">
                          {carton.code}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          Thùng {carton.cartonIndex} / {carton.totalCartons || "—"}
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-medium text-indigo-600">
                          {formatNumber(carton.quantity || 0)}
                        </td>
                        <td className="py-2 px-3">
                          {carton.isDispatched ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] font-semibold">
                              <CheckCircle2 className="h-3 w-3 mr-1" /> ĐÃ LÊN XE
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px]">
                              CHỜ QUÉT
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 px-3 text-[11px] text-slate-500 font-mono">
                          {carton.dispatchedAt
                            ? new Date(carton.dispatchedAt).toLocaleTimeString("vi-VN") + " " + new Date(carton.dispatchedAt).toLocaleDateString("vi-VN")
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="text-xs"
          >
            Đóng Trạm Quét
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
